// Library Methods
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";

// Components
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import { useTheme } from "@mui/material/styles";
import { CaretDownIcon } from "src/assets/icons";

// Styles
import {
  CustomTextFieldProps,
  getMenuItemStateColors,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

type ItemType = {
  id?: string;
  name?: string;
};

type SearchBoxType = {
  id?: string;
  data: Array<ItemType>;
  state: ItemType;
  setState: React.Dispatch<React.SetStateAction<ItemType>>;
  maxHeight?: string;
  defaultShowList?: boolean;
  parentOpen?: boolean;
};

const SearchBox = ({
  id,
  data = [],
  state,
  setState,
  maxHeight = "200px",
  defaultShowList = false,
  parentOpen = false,
}: SearchBoxType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface, onSurface, inputBorder } = getSurfaceTokens(theme);
  const { selected: selectedItemColor, hover: hoverItemColor } =
    getMenuItemStateColors(theme);

  const [valueOfSearchBox, setValueOfSearchBox] = useState("");
  const [showList, setShowList] = useState(defaultShowList);
  const [isFocused, setIsFocused] = useState(false);
  const [mouseIsOverList, setMouseIsOverList] = useState(false);

  const stateRef = useRef<string | null>(null);
  const textFieldRef = useRef<HTMLInputElement | null>(null);
  const searchTermRef = useRef("");

  const filteredItems = data.filter((item) => {
    const term = searchTermRef.current.toLowerCase();
    return item?.name?.toLowerCase().includes(term);
  });

  const handleItemClick = useCallback(
    (item: ItemType) => {
      searchTermRef.current = "";
      stateRef.current = item?.name ?? null;
      setState(item);
      setShowList(false);
      setIsFocused(false);
    },
    [setState]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      searchTermRef.current = value;
      setState({});
      stateRef.current = value;
      setShowList(true);
      setIsFocused(true);
      setValueOfSearchBox(value); // Update only for display purposes
    },
    [setState]
  );

  const handleInputFocus = useCallback(() => {
    setShowList(true);
    setIsFocused(true);
  }, []);

  const handleMouseEnter = useCallback(() => {
    setMouseIsOverList(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setMouseIsOverList(false);
  }, []);

  const handleInputBlur = useCallback(() => {
    if (stateRef.current) {
      const corresponding = data.find(
        (datum) => datum.name === stateRef.current
      );
      setState(
        corresponding ?? {
          name: stateRef.current,
        }
      );
    }
    setTimeout(() => {
      if (!mouseIsOverList) {
        setShowList(false);
        setIsFocused(false);
      }
    }, 100);
  }, [data, setState, mouseIsOverList]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, item: ItemType) => {
      e.preventDefault();
      handleItemClick(item);
    },
    [handleItemClick]
  );

  useEffect(() => {
    if (parentOpen && defaultShowList && textFieldRef?.current) {
      setTimeout(() => {
        textFieldRef.current?.focus();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentOpen]);

  return (
    <>
      <TextField
        id={id ?? "custom-search-box"}
        data-testid="custom-search-box"
        fullWidth
        variant="outlined"
        color="warning"
        autoComplete="off"
        placeholder={t("Enter a value")}
        inputProps={{
          sx: {
            "&::placeholder": {
              color: inputBorder,
              opacity: 1,
            },
          },
        }}
        InputProps={{
          sx: {
            ...CustomTextFieldProps(theme),
            paddingRight: 0,
            overflowY: "hidden",
            ".MuiInputBase-input": { padding: "0px 16px" },
            ".MuiOutlinedInput-notchedOutline": {
              border: "1px solid #5B6D7A",
            },
          },
          endAdornment: (
            <InputAdornment position="start">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => {
                  if (showList && isFocused) {
                    handleInputBlur();
                  } else {
                    handleInputFocus();
                    // set the focus so the list closes when clicking outside of it
                    textFieldRef.current?.focus();
                  }
                }}
              >
                <CaretDownIcon />
              </Box>
            </InputAdornment>
          ),
        }}
        value={valueOfSearchBox}
        onChange={handleChange}
        inputRef={textFieldRef}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
      />

      {showList && (
        <Box
          sx={{
            width: "100%",
            maxHeight,
            overflowY: "auto",
            overflowX: "hidden",
            py: "16px",
            backgroundColor: surface,
            color: onSurface,
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {filteredItems?.length > 0 ? (
            filteredItems.map((item, index) => (
              <Box
                key={index}
                onMouseDown={(e) => handleMouseDown(e, item)}
                aria-label={item?.id}
                sx={{
                  width: "100%",
                  padding: "8px 16px",
                  backgroundColor:
                    item?.id === state?.id ? selectedItemColor : "transparent",

                  "&:hover": {
                    cursor: "pointer",
                    backgroundColor:
                      item?.id === state?.id
                        ? selectedItemColor
                        : hoverItemColor,
                  },
                }}
              >
                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "14px",
                  }}
                >
                  {item?.name}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ padding: "8px 16px", fontSize: "14px" }}>
              {t("No results found.")}
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};

export default SearchBox;
